// CollaborationLinksQuadrant — Bottom-right quadrant: Links (Journals + Patents grouped)
import { useState, useEffect, useMemo } from 'react'
import { Link2, BookOpen, FileText, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react'
import { useCollaborationFilter } from '../../hooks/useCollaborationFilter'
import type { CollaborationLink, DetailItem } from '../../types/collaboration'

const PAGE_SIZE = 10

function ScoreBadge({ score }: { score: number }) {
  const color = score > 80 ? 'bg-green-100 text-green-700' : score > 60 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
  return <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${color}`}>{score}%</span>
}

interface Props {
  onDetailClick: (item: DetailItem) => void
}

export default function CollaborationLinksQuadrant({ onDetailClick }: Props) {
  const { filteredLinks } = useCollaborationFilter()
  const [page, setPage] = useState(0)

  const totalPages = Math.ceil(filteredLinks.length / PAGE_SIZE)
  const safePage = Math.min(page, Math.max(0, totalPages - 1))
  const pagedLinks = filteredLinks.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE)

  // Reset page when filtered items change
  useEffect(() => { setPage(0) }, [filteredLinks.length])

  const { journals, patents } = useMemo(() => ({
    journals: pagedLinks.filter(l => l.type === 'journal'),
    patents: pagedLinks.filter(l => l.type === 'patent'),
  }), [pagedLinks])

  const handleClick = (link: CollaborationLink) => {
    onDetailClick({ type: 'link', item: link })
  }

  return (
    <div className="light-card flex flex-col" style={{ maxHeight: '420px' }}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-sky-200/50">
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-iflow-600" />
          <h3 className="text-sm font-semibold text-gray-900">Links</h3>
          <span className="px-1.5 py-0.5 text-xs rounded-full bg-sky-500/10 text-gray-600 font-medium">{filteredLinks.length}</span>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {filteredLinks.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No links match the current filter.</p>
        ) : (
          <div className="space-y-2">
            {journals.length > 0 && (
              <>
                <div className="flex items-center gap-1.5 px-2 pt-1">
                  <BookOpen className="w-3 h-3 text-purple-500" />
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Journals</span>
                </div>
                {journals.map(link => (
                  <div
                    key={link.id}
                    onClick={() => handleClick(link)}
                    className="flex items-start gap-2 px-3 py-2 rounded-lg border-l-3 border-purple-400 hover:bg-sky-500/5 cursor-pointer"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium text-gray-900 truncate">{link.title}</p>
                        {link.notes?.startsWith('source: ai_') && (
                          <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-blue-50 text-blue-600 font-medium shrink-0">AI</span>
                        )}
                      </div>
                      {link.doi && <p className="text-xs text-gray-500 truncate">DOI: {link.doi}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {link.similarityScore != null && <ScoreBadge score={link.similarityScore} />}
                      {link.doi && (
                        <a
                          href={`https://doi.org/${link.doi}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-500 hover:text-iflow-500"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
            {patents.length > 0 && (
              <>
                <div className="flex items-center gap-1.5 px-2 pt-1">
                  <FileText className="w-3 h-3 text-amber-500" />
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Patents</span>
                </div>
                {patents.map(link => (
                  <div
                    key={link.id}
                    onClick={() => handleClick(link)}
                    className="flex items-start gap-2 px-3 py-2 rounded-lg border-l-3 border-amber-400 hover:bg-sky-500/5 cursor-pointer"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium text-gray-900 truncate">{link.title}</p>
                        {link.notes?.startsWith('source: ai_') && (
                          <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-blue-50 text-blue-600 font-medium shrink-0">AI</span>
                        )}
                      </div>
                      {link.filing && <p className="text-xs text-gray-500 truncate">Filing: {link.filing}</p>}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 text-xs text-gray-500">
          <button
            disabled={safePage === 0}
            onClick={() => setPage(p => p - 1)}
            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-3 h-3" /> Prev
          </button>
          <span>{safePage + 1} of {totalPages}</span>
          <button
            disabled={safePage >= totalPages - 1}
            onClick={() => setPage(p => p + 1)}
            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Next <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  )
}
